// Home.jsx
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  Avatar,
  Rating,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  LockOutlined,
  CloudDoneOutlined,
  MicNoneOutlined,
} from "@mui/icons-material";
import { useEffect } from "react";
import locales from "../locales.json";
import { useLang } from "../context/LangContext";
import { canUseGoogleAds, reportGoogleAdsPageView } from "../utils/googleAds";

/** Full-bleed wrapper so we can style background bands edge-to-edge */
function FullBleed({ children, sx }) {
  return (
    <Box
      sx={{
        position: "relative",
        left: "50%",
        right: "50%",
        marginLeft: "-50vw",
        marginRight: "-50vw",
        width: "100vw",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default function MedictaHome() {
  const { lang } = useLang();
  const { home } = locales;

  useEffect(() => {
    if (canUseGoogleAds()) {
      reportGoogleAdsPageView();
    } else {
      window.addEventListener(
        "google-ads-consent-granted",
        reportGoogleAdsPageView,
      );
      return () =>
        window.removeEventListener(
          "google-ads-consent-granted",
          reportGoogleAdsPageView,
        );
    }
  }, []);

  const t = {
    title:
      home?.title?.[lang] ||
      "Libérez un temps précieux grâce à une transcription par reconnaissance vocale basée sur l’IA, 100% hébergé en Suisse de manière sécurisée.",
    heroTagline:
      home?.hero_tagline?.[lang] ||
      "Rédigez vos rapports directement dans votre logiciel médical",
    cta: home?.get_started?.[lang] || "Essayez Medicta gratuitement",
    howTitle: home?.how_it_works?.[lang] || "Comment ça marche ?",
    install_caption:
      home?.install_caption?.[lang] ||
      "Installation en 2 minutes. Aucune carte requise.",
    secure: home?.badge_secure?.[lang] || "Chiffrement de bout en bout",
    swiss: home?.badge_swiss?.[lang] || "Serveurs en Suisse",
    realtime: home?.badge_realtime?.[lang] || "Reconnaissance en temps réel",
    proof: home?.proof?.[lang] || [
      { label: "Données non utilisées pour l’entraînement", value: "" },
      { label: "100% hébergé en Suisse", value: "" },
      { label: "Assistance humaine en Suisse", value: "" },
    ],
    steps: [
      {
        title: home?.step1?.[lang] || "Parlez naturellement",
        desc:
          home?.step1_description?.[lang] ||
          "Vous dictez vos rapports sans effort, l’application capte chaque mot avec clarté.",
      },
      {
        title: home?.step2?.[lang] || "Laissez l’IA travailler",
        desc:
          home?.step2_description?.[lang] ||
          "Vous bénéficiez d’une reconnaissance vocale suisse, rapide et précise, qui transforme vos paroles en texte fiable.",
      },
      {
        title: home?.step3?.[lang] || "Gagnez du temps",
        desc:
          home?.step3_description?.[lang] ||
          "Vous retrouvez instantanément votre transcription intégrée directement dans votre application médicale.",
      },
    ],
    testimonialsTitle:
      home?.testimonials_title?.[lang] || "Témoignages de médecins",
    testimonials: home?.testimonials?.[lang] || [
      {
        name: "Dr M. — Genève",
        role: "Médecin généraliste",
        quote:
          "Je gagne 1 à 2 heures par jour sur mes comptes-rendus. La précision sur les termes médicaux est bluffante.",
        avatar: "M",
      },
      {
        name: "Dr M.-L. — Lausanne",
        role: "Gynécologie",
        quote:
          "Intégration fluide à mon logiciel. Je parle, c’est transcrit, je relis et j’envoie. Très fiable.",
        avatar: "L",
      },
      {
        name: "Dr M. M. — Fribourg",
        role: "Pédiatrie",
        quote:
          "Les consultations sont plus naturelles, je regarde mes patients au lieu du clavier.",
        avatar: "F",
      },
    ],
  };

  return (
    <Box>
      {/* ================= HERO ================= */}
      <FullBleed
        sx={{
          position: "relative",
          overflow: "hidden",
          py: { xs: 10, md: 14 },
          marginTop: { xs: -2, md: -2 },
          minHeight: { md: 400, lg: 400 },
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          // slightly stronger base tint
          background:
            "linear-gradient(135deg, rgba(59,130,246,.16), rgba(168,85,247,.16))",

          // LEFT photo (full height, fades toward center)
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundImage: "url('/doctor-left.jpg')",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "-60px center",
            backgroundSize: { xs: "0 0", md: "auto 100%" }, // full height on md+
            opacity: { md: 0.28, lg: 0.26 },
            filter: "grayscale(100%) contrast(.95) saturate(.85)",
            mixBlendMode: "normal",
            pointerEvents: "none",
          },

          // RIGHT photo (mirror)
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundImage: "url('/doctor-right.webp')",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right center",
            backgroundSize: { xs: "0 0", md: "auto 100%" },
            opacity: { md: 0.28, lg: 0.26 },
            filter: "grayscale(100%) contrast(.95) saturate(.85)",
            mixBlendMode: "normal",
            pointerEvents: "none",
          },
        }}
      >
        {/* Overlay SCRIM above photos (side fades + central tint) */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            pointerEvents: "none",
            background:
              "linear-gradient(90deg,rgba(143, 143, 143, 0.01) 0%, #dadef8ff 50%, rgba(150, 150, 150, 0.01) 100%)",
            backgroundRepeat: "no-repeat, no-repeat",
            backgroundPosition: "center, center",
            backgroundSize: "100% 100%, 100% 100%",
            mixBlendMode: "normal",
          }}
        />

        {/* Hero content above overlay */}
        <Container
          maxWidth="xl"
          disableGutters
          sx={{ position: "relative", zIndex: 2 }}
        >
          <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 } }}>
            <Grid container spacing={6} justifyContent="center">
              <Grid item xs={12} md={10} lg={9}>
                <Stack spacing={3} textAlign="center">
                  <Typography
                    component="h1"
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      lineHeight: { xs: 1.15, md: 1.18 },
                      letterSpacing: { xs: 0, md: 0.2 },
                      fontSize: {
                        xs: "1.9rem",
                        sm: "2.3rem",
                        md: "2.9rem",
                        lg: "3.2rem",
                      },
                      color: "text.primary",
                      maxWidth: 980,
                      mx: "auto",
                      "@supports (text-wrap: balance)": { textWrap: "balance" },
                    }}
                  >
                    {t.title}
                  </Typography>

                  <Typography
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: "1.05rem", md: "1.2rem" },
                      letterSpacing: ".2px",
                    }}
                  >
                    {t.heroTagline}
                  </Typography>

                  <Stack justifyContent="center" sx={{ pt: 1 }}>
                    <Button
                      component={RouterLink}
                      to="/license"
                      variant="contained"
                      size="large"
                      sx={{
                        borderRadius: 999,
                        px: 4,
                        fontWeight: "bold",
                        alignSelf: "center",
                      }}
                    >
                      {t.cta}
                    </Button>
                  </Stack>

                  <Typography variant="body2" color="text.secondary">
                    {t.install_caption}
                  </Typography>

                  {/* Trust chips */}
                  <Stack
                    direction="row"
                    spacing={1.5}
                    flexWrap="wrap"
                    justifyContent="center"
                  >
                    <Chip
                      color="info"
                      icon={<LockOutlined />}
                      label={t.secure}
                    />
                    <Chip
                      color="info"
                      icon={<CloudDoneOutlined />}
                      label={t.swiss}
                    />
                    <Chip
                      color="info"
                      icon={<MicNoneOutlined />}
                      label={t.realtime}
                    />
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Container>
      </FullBleed>

      {/* ================= HOW IT WORKS ================= */}
      <FullBleed
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? "#ffffff"
              : "rgba(255,255,255,0.02)",
          py: { xs: 8, md: 10 },
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, textAlign: "center", mb: 4 }}
          >
            {t.howTitle}
          </Typography>

          <Grid container spacing={4}>
            {t.steps.map((s, i) => (
              <Grid key={i} item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    textAlign: "center",
                    p: 2,
                    transition: "transform .2s ease, box-shadow .2s ease",
                    "&:hover": { transform: "translateY(-3px)", boxShadow: 4 },
                  }}
                >
                  <CardContent>
                    <Avatar
                      sx={{
                        bgcolor: "primary.main",
                        color: "white",
                        width: 40,
                        height: 40,
                        mb: 2,
                        mx: "auto",
                        fontWeight: 700,
                      }}
                    >
                      {i + 1}
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {s.title}
                    </Typography>
                    <Typography color="text.secondary">{s.desc}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </FullBleed>

      {/* ================= TESTIMONIALS ================= */}
      <FullBleed
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? "#F6F8FF"
              : "rgba(255,255,255,0.04)",
          py: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, textAlign: "center", mb: 4 }}
          >
            {t.testimonialsTitle}
          </Typography>

          <Grid container spacing={3}>
            {t.testimonials.map((tm, i) => (
              <Grid key={i} item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                  }}
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Avatar sx={{ bgcolor: "primary.main" }}>
                        {tm.avatar}
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 700 }}>
                          {tm.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {tm.role}
                        </Typography>
                      </Box>
                    </Stack>
                    <Rating value={5} readOnly size="small" sx={{ mb: 1 }} />
                    <Typography sx={{ fontStyle: "italic" }}>
                      “{tm.quote}”
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </FullBleed>
    </Box>
  );
}
